import { createElement, useEffect, useMemo } from 'react';
import { useStore } from '@nanostores/react';

import { useCareStateDomain, type ClinicalScopeT } from '@/entities/care-state-domain';
import { useUserInfoQuery } from '@/entities/user-info';
import { useUserPhrQuery } from '@/entities/user-phr';
import { ProblemHistoryTab } from '@/features/care-state-problems-tab';
import { RequestsListTab } from '@/features/care-state-requests-tab';
import { PatientHealthRecordsTab } from '@/features/patient-health-records-tab';
import { PatientFaxTab } from '@/features/patient-fax-tab';
import { PatientReferralsTab } from '@/features/patient-referrals-tab';
import { PatientLabsTab } from '@/features/patient-labs-tab';
import { PatientMedicationsTab } from '@/features/patient-medications-tab';
import { PatientRxRequestsTab } from '@/features/patient-rx-requests-tab';
import { PatientPriorAuthsTab } from '@/features/prior-auth-tab';
import { SANDBOX_SIMPLIFIED_WORKSPACE_ENABLED } from '@/shared/config';
import { $patientPanelSelectedTab } from '@/shared/stores';

import { LabsTabLabel } from '../ui/LabsTabLabel';
import {
  PATIENT_HEALTH_RECORDS_TAB_ID,
  PATIENT_FAX_TAB_ID,
  PATIENT_REFERRALS_TAB_ID,
  PATIENT_LAB_ORDERS_TAB_ID,
  PATIENT_PRIOR_AUTHS_TAB_ID,
  PATIENT_PROBLEM_HISTORY_TAB_ID,
  PATIENT_REQUESTS_TAB_ID,
  PATIENT_RX_INBOX_TAB_ID,
  PATIENT_RX_ORDERS_TAB_ID,
  type PatientWorkspaceTabId,
} from './tabIds';
import type { PatientWorkspaceTab } from './types';

const TAB_TO_SCOPE_TYPE: Readonly<Partial<Record<PatientWorkspaceTabId, ClinicalScopeT['type']>>> =
  {
    [PATIENT_REQUESTS_TAB_ID]: 'request',
    [PATIENT_PROBLEM_HISTORY_TAB_ID]: 'problem',
  };

export function usePatientWorkspaceTabs(
  userId: string | undefined,
  options: { onCreateFax?: () => void } = {}
) {
  const phrQuery = useUserPhrQuery(userId);
  const userInfoQuery = useUserInfoQuery(userId);
  const { scope, setScope } = useCareStateDomain();
  const selectedTab = useStore($patientPanelSelectedTab);

  // Keep the agent-facing scope type in sync with whichever tab the clinician
  // (or copilot) is currently viewing. Tabs without a mapped scope leave the
  // current value untouched.
  useEffect(() => {
    if (selectedTab === null) {
      return;
    }
    const next = TAB_TO_SCOPE_TYPE[selectedTab as PatientWorkspaceTabId];
    if (next === undefined || next === scope.type) {
      return;
    }
    setScope({ type: next });
  }, [selectedTab, scope.type, setScope]);

  const insurance = userInfoQuery.data?.patientInfo?.insurance;
  const hasInsurance = insurance != null && insurance !== '';

  const tabs = useMemo<PatientWorkspaceTab[]>(() => {
    const resolvedUserId = userId ?? '';

    const requestsTab: PatientWorkspaceTab = {
      id: PATIENT_REQUESTS_TAB_ID,
      label: 'Requests',
      content: createElement(RequestsListTab, { userId: resolvedUserId }),
    };

    const problemHistoryTab: PatientWorkspaceTab = {
      id: PATIENT_PROBLEM_HISTORY_TAB_ID,
      label: 'Problem History',
      content: createElement(ProblemHistoryTab, { userId: resolvedUserId }),
    };

    if (!userId) {
      return [requestsTab, problemHistoryTab];
    }

    const rxOrdersTab: PatientWorkspaceTab = {
      id: PATIENT_RX_ORDERS_TAB_ID,
      label: 'Rx Orders',
      content: createElement(PatientMedicationsTab, { userId: resolvedUserId }),
    };

    const labOrdersTab: PatientWorkspaceTab = {
      id: PATIENT_LAB_ORDERS_TAB_ID,
      label: createElement(LabsTabLabel, { hasInsurance }),
      content: createElement(PatientLabsTab, { userId: resolvedUserId }),
    };

    const healthRecordsTab: PatientWorkspaceTab = {
      id: PATIENT_HEALTH_RECORDS_TAB_ID,
      label: 'Health Records',
      content: createElement(PatientHealthRecordsTab, { userId: resolvedUserId }),
    };

    const priorAuthsTab: PatientWorkspaceTab = {
      id: PATIENT_PRIOR_AUTHS_TAB_ID,
      label: 'Prior Auths',
      content: createElement(PatientPriorAuthsTab, { userId: resolvedUserId }),
    };

    const rxInboxTab: PatientWorkspaceTab = {
      id: PATIENT_RX_INBOX_TAB_ID,
      label: 'Rx Inbox',
      content: createElement(PatientRxRequestsTab, { userId: resolvedUserId }),
    };

    const faxTab: PatientWorkspaceTab = {
      id: PATIENT_FAX_TAB_ID,
      label: 'Fax',
      content: createElement(PatientFaxTab, {
        userId: resolvedUserId,
        onCreate: options.onCreateFax,
      }),
    };

    const referralsTab: PatientWorkspaceTab = {
      id: PATIENT_REFERRALS_TAB_ID,
      label: 'Referrals',
      content: createElement(PatientReferralsTab, { userId: resolvedUserId }),
    };

    return [
      requestsTab,
      problemHistoryTab,
      rxOrdersTab,
      labOrdersTab,
      healthRecordsTab,
      ...(SANDBOX_SIMPLIFIED_WORKSPACE_ENABLED
        ? []
        : [priorAuthsTab, rxInboxTab, faxTab, referralsTab]),
    ];
  }, [options.onCreateFax, userId, hasInsurance]);

  return { tabs, phrQuery };
}
